import { Button } from "@/components/ui/button";
import { useGenerateQrCode } from "@/hooks/useGenerateQrCode";
import { useMounted } from "@/hooks/useMounted";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import SpinnerSvg from "@assets/images/loader_spinner.svg";
import { isMobile } from "react-device-detect";
import { sendContentsLog, withLog } from "@/lib/a2s";
import QrGuideDialog from "@/components/dialog/QrGuideDialog";
import { getAccessCode } from "@/lib/utils";
import { getIsGetoAccessCode } from "@/lib/geto";
import { ACCESS_CODE_SEARCH_PARAM } from "@/consts";

const Timer = ({ availableTime }: { availableTime: number }) => {
  const minutes = Math.floor(availableTime / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (availableTime % 60).toString().padStart(2, "0");
  return (
    <p className="text-sm text-gray-600">
      유효시간 {minutes}:{seconds}
    </p>
  );
};

const QRLogin = () => {
  const mounted = useMounted();
  const [loadedImage, setLoadedImage] = useState(false);
  const { state, errorCode, qrCodeUrl, availableTime, fetch } = useGenerateQrCode();

  const isGeto = getIsGetoAccessCode();

  const handleButtonClick = () => {
    fetch();
  };

  const handleNexonPlayClick = () => {
    const baseUrl = isMobile ? import.meta.env.VITE_NEXON_PLAY_MOBILE_URL : import.meta.env.VITE_NEXON_PLAY_PC_URL;
    const url = new URL(baseUrl);

    if (isGeto) {
      const accessCode = getAccessCode();
      url.searchParams.set(ACCESS_CODE_SEARCH_PARAM, accessCode.toString());
    }

    window.location.href = url.toString();
  };

  const handleQRGuideClick = () => {};

  const getErrorMessage = () => {
    switch (errorCode) {
      case "qr_auth_timeout":
        return (
          <>
            <p>유효시간이 초과되었습니다.</p>
            <p>QR로그인을 다시 시도해 주세요.</p>
          </>
        );
      case "qr_auth_canceled":
      case "qr_auth_failed":
        return (
          <>
            <p>넥슨플레이에서 바이오인증이 승인되지 않았습니다.</p>
            <p>QR로그인을 다시 시도해 주세요.</p>
          </>
        );
      case "mismatch_client_ip":
        return (
          <>
            <p>로그인한 IP주소와 현재 IP주소가 일치하지 않아 로그아웃 됩니다.</p>
            <p>넥슨ID 보호를 위해 다시 로그인 해주세요.</p>
            <p className="text-gray-700">( IP 변경이 일어날 수 있는 서비스나 기능을 사용하는 경우, 해제 후 이용해 주세요. )</p>
          </>
        );
      case "blocked_signin_with_qr":
        return (
          <>
            <p>잘못된 요청으로 24시간동안 이용이 제한됩니다.</p>
            <p>서비스 이용을 원하신다면 넥슨 ID와 비밀번호로 로그인해주시기 바랍니다.</p>
          </>
        );
      default:
        return (
          <>
            <p>QR코드를 정상적으로 불러오지 못했습니다.</p>
            <p>QR로그인을 다시 시도해 주세요.</p>
          </>
        );
    }
  };

  useEffect(() => {
    if (mounted) {
      fetch();
    }
  }, [mounted, fetch]);

  useEffect(() => {
    if (state === "loading") {
      sendContentsLog("LOGIN0021"); // 로딩 스피너 노출 시 로그
      setLoadedImage(false);
    } else if (state === "success") {
      sendContentsLog("LOGIN0022"); // QR 이미지 로드 완료 시 로그
    } else if (errorCode === "blocked_signin_with_qr") {
      sendContentsLog("LOGIN0019"); // QR 인증 차단
    } else if (errorCode === "qr_auth_timeout") {
      sendContentsLog("LOGIN0023"); // QR 인증 타임아웃 시 로그
    } else if (errorCode === "qr_auth_failed" || errorCode === "qr_auth_canceled") {
      sendContentsLog("LOGIN0024"); // QR 인증 실패 시 로그
    } else if (errorCode === "mismatch_client_ip") {
      sendContentsLog("LOGIN0025"); // 클라이언트 IP 불일치 시 로그
    } else {
      sendContentsLog("LOGIN0020"); // QR 코드 로드 실패 시 로그
    }
  }, [state, errorCode]);

  return (
    <>
      <div className="w-full">
        <div className="mb-7 mt-8 md:mb-8 md:mt-9">
          <p className="text-pretty text-center text-gray-900">
            <span className="break-keep min-[430px]:inline-block">
              넥슨플레이 &gt; QR 스캐너 &gt; QR 코드 촬영 &gt; 바이오 인증을 진행해 주세요.{" "}
            </span>
          </p>
        </div>

        {state === "error" && <div className="text-balance break-keep bg-gray-200 p-6 text-center font-medium">{getErrorMessage()}</div>}
        {state !== "error" && (
          <div className="flex flex-col items-center space-y-3">
            <div className="flex items-center justify-center">
              <div className="size-[161px]">
                {!loadedImage && (
                  <div className="relative flex size-full items-center justify-center">
                    <div className="absolute size-[100px] animate-spin">
                      <img src={SpinnerSvg} width={100} height={100} alt="spinner" />
                    </div>
                  </div>
                )}
                {state === "success" && (
                  <img
                    src={qrCodeUrl}
                    alt="qrcode_image"
                    className={loadedImage ? "block" : "hidden"}
                    onLoad={() => setLoadedImage(true)}
                  />
                )}
              </div>
            </div>
            {state == "success" && loadedImage && <Timer availableTime={availableTime} />}
          </div>
        )}

        {state !== "error" && <div className="my-8 h-px bg-gray-300 md:my-10" />}
        {state === "error" && errorCode !== "blocked_signin_with_qr" && (
          <Button variant={"black"} className="mt-6 md:mt-8" onClick={withLog("LOGIN0029", handleButtonClick)}>
            QR 로그인 재시도
          </Button>
        )}

        <div className="mt-8 space-y-1.5 md:mt-10">
          <div className="flex justify-between">
            <p className="text-sm font-bold text-gray-700">QR 로그인 이용 가이드</p>
            <Button
              variant="link"
              className="size-fit gap-0 p-0 font-medium [&_svg]:size-3.5"
              onClick={withLog("LOGIN0027", handleNexonPlayClick)}
            >
              넥슨플레이 안내
              <ChevronRight />
            </Button>
          </div>
          <ul className="relative w-full list-outside list-none ps-2 text-sm text-gray-600">
            {state === "success" && (
              <>
                <li className="pl-1 before:absolute before:left-0 before:content-['※']">
                  <span className="mr-2">모바일기기로 QR코드를 촬영해 주세요.</span>
                  <QrGuideDialog>
                    <Button
                      variant={"link"}
                      size={"fit"}
                      className="text-sm font-normal text-blue-600"
                      onClick={withLog("LOGIN0028", handleQRGuideClick)}
                    >
                      QR 스캐너 위치 보기
                    </Button>
                  </QrGuideDialog>
                </li>
                <li className="pl-1 before:absolute before:left-0 before:content-['※']">
                  바이오인증의 인증 승인 페이지에서 지문인식을 해주세요. (또는 Face ID){" "}
                </li>
              </>
            )}
            {state !== "success" && (
              <>
                <li className="pl-1 before:absolute before:left-0 before:content-['※']">
                  <span className="mr-2">모바일기기로 QR코드를 촬영해 주세요.</span>
                  <QrGuideDialog>
                    <Button
                      variant={"link"}
                      size={"fit"}
                      className="text-sm font-normal text-blue-600"
                      onClick={withLog("LOGIN0028", handleQRGuideClick)}
                    >
                      QR 스캐너 위치 보기
                    </Button>
                  </QrGuideDialog>
                </li>
                <li className="pl-1 before:absolute before:left-0 before:content-['※']">
                  바이오인증의 인증 승인 페이지에서 지문인식을 해주세요. (또는 Face ID)
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

export default QRLogin;
